import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setProfile } from '../store/userSlice';
import { fetchMyProfile, updateMyProfile } from '../services/profile';
import { getCookie } from '../utils/helpers';
import {
  getSupabaseSession,
  getFreshSupabaseSession,
  syncSupabaseUserToBackend,
} from '../services/auth';

export default function ProfileCard() {
  const toUiGender = (value) => {
    const v = (value || '').toString().trim().toLowerCase();
    if (v === 'male' || v === 'female' || v === 'other') return v;
    if (value === 'Male') return 'male';
    if (value === 'Female') return 'female';
    if (value === 'Other') return 'other';
    return 'female';
  };
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user);
  const storedName = localStorage.getItem('userFullName') || '';
  const storedEmail = localStorage.getItem('userEmail') || '';
  const storedGender = localStorage.getItem('userGender') || '';
  const storedDOB = localStorage.getItem('userDOB') || '';
  const [fullName, setFullName] = useState(
    useSelector((s) => s.user.fullName) || storedName
  );
  const initials = useMemo(() => {
    const base = (fullName || storedName || '').trim();
    const parts = base.split(' ').filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [fullName, storedName]);

  const [gender, setGender] = useState(
    toUiGender(storedGender || user.gender || 'female')
  );
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [email, setEmail] = useState(
    user.email || localStorage.getItem('userEmail') || ''
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailLocked, setEmailLocked] = useState(false);
  const [dobError, setDobError] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [streetName, setStreetName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [telephoneNumber, setTelephoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const fullNameRef = useRef(null);
  const emailRef = useRef(null);
  const genderRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const yearRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = useMemo(
    () =>
      Array.from({ length: 100 }, (_, i) => `${new Date().getFullYear() - i}`),
    []
  );

  const userRole = user.role || localStorage.getItem('userRole') || '';
  const isPatient = userRole === 'Patient';

  const isPasswordInvalid = useMemo(() => {
    if (!showPasswordFields) return false;
    if (!password && !confirmPassword) return false;
    if (password && password.length < 8) return true;
    if (password && !confirmPassword) return true;
    if (password && confirmPassword && password !== confirmPassword)
      return true;
    return false;
  }, [showPasswordFields, password, confirmPassword]);

  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      try {
        setLoading(true);
        const profile = await fetchMyProfile();
        if (cancelled) return;
        dispatch(setProfile(profile));
        setFullName(profile.fullName || '');
        setEmail(profile.email || '');
        setGender(toUiGender(profile.gender || 'female'));
        setStreetNumber(profile.streetNumber ?? '');
        setStreetName(profile.streetName ?? '');
        setPostalCode(profile.postalCode ?? '');
        setCity(profile.city ?? '');
        setTelephoneNumber(profile.telephoneNumber ?? '');
        if (profile.dateOfBirth) {
          const dob = new Date(profile.dateOfBirth);
          setMonth(months[dob.getMonth()]);
          setDay(`${dob.getDate()}`);
          setYear(`${dob.getFullYear()}`);
        }
      } catch (e) {
        const merged = {
          fullName: user.fullName || storedName || '',
          email: user.email || storedEmail || '',
          gender: toUiGender(user.gender || storedGender || 'female'),
          dateOfBirth: user.dateOfBirth || storedDOB || null,
          streetNumber: user.streetNumber ?? '',
          streetName: user.streetName ?? '',
          postalCode: user.postalCode ?? '',
          city: user.city ?? '',
          telephoneNumber: user.telephoneNumber ?? '',
        };
        if (cancelled) return;
        dispatch(setProfile(merged));
        setFullName(merged.fullName);
        setEmail(merged.email);
        setGender(toUiGender(merged.gender));
        setStreetNumber(merged.streetNumber);
        setStreetName(merged.streetName);
        setPostalCode(merged.postalCode);
        setCity(merged.city);
        setTelephoneNumber(merged.telephoneNumber);
        if (merged.dateOfBirth) {
          const dob = new Date(merged.dateOfBirth);
          setMonth(months[dob.getMonth()]);
          setDay(`${dob.getDate()}`);
          setYear(`${dob.getFullYear()}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    hydrate();
    (async () => {
      try {
        const session = await getSupabaseSession();
        const provider = session?.user?.app_metadata?.provider;
        if (!cancelled) setEmailLocked(provider === 'google');
      } catch (e) {
        if (!cancelled) setEmailLocked(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      setDobError('');
      const currentFullName = fullNameRef.current
        ? fullNameRef.current.value
        : fullName;
      const currentEmail = emailRef.current ? emailRef.current.value : email;
      const currentGender = genderRef.current
        ? genderRef.current.value
        : gender;
      const selMonth = monthRef.current ? monthRef.current.value : month;
      const selDay = dayRef.current ? dayRef.current.value : day;
      const selYear = yearRef.current ? yearRef.current.value : year;

      const monthIndex = months.indexOf(selMonth);
      let dobDate = null;
      if (selMonth || selDay || selYear) {
        if (!(selMonth && selDay && selYear)) {
          setDobError('Please enter a valid date of birth.');
          return;
        }
        const y = parseInt(selYear, 10);
        const m = monthIndex; // 0-based
        const d = parseInt(selDay, 10);
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
          setDobError('Please enter a valid date of birth.');
          return;
        }
        const maxDaysInMonth = (() => {
          if (m === 1) {
            const isLeap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
            return isLeap ? 29 : 28;
          }
          return [3, 5, 8, 10].includes(m) ? 30 : 31;
        })();
        if (d < 1 || d > maxDaysInMonth) {
          setDobError('Please enter a valid date of birth.');
          return;
        }
        const dt = new Date(y, m, d);
        if (
          dt.getFullYear() !== y ||
          dt.getMonth() !== m ||
          dt.getDate() !== d
        ) {
          setDobError('Please enter a valid date of birth.');
          return;
        }
        dobDate = dt;
      }
      const yyyyMmDd = dobDate
        ? `${dobDate.getFullYear()}-${String(dobDate.getMonth() + 1).padStart(2, '0')}-${String(dobDate.getDate()).padStart(2, '0')}`
        : null;
      const payload = {
        fullName: currentFullName,
        gender: currentGender,
        dateOfBirth: yyyyMmDd,
        streetNumber: streetNumber.trim() || undefined,
        streetName: streetName.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        city: city.trim() || undefined,
        telephoneNumber: telephoneNumber.trim() || undefined,
      };
      if (!emailLocked) {
        payload.email = currentEmail;
      }

      const currentPassword = showPasswordFields
        ? passwordRef.current
          ? passwordRef.current.value
          : password
        : '';
      if (showPasswordFields && currentPassword && currentPassword.length >= 8) {
        payload.password = currentPassword;
      }

      let access = getCookie('access');
      if (!access) {
        // Refresh the Supabase token before re-syncing; a stale token would be
        // rejected with 401 (see getFreshSupabaseSession).
        const session = await getFreshSupabaseSession();
        if (session) {
          try {
            await syncSupabaseUserToBackend(session);
          } catch (err) {
            // ignore sync errors, continue with profile update
          }
        }
      }
      const updated = await updateMyProfile(payload);
      dispatch(setProfile(updated));
      localStorage.setItem('userFullName', updated.fullName || '');
      localStorage.setItem('userEmail', updated.email || '');
      localStorage.setItem('userGender', toUiGender(updated.gender || ''));
      if (updated.dateOfBirth) {
        localStorage.setItem('userDOB', updated.dateOfBirth);
      } else {
        localStorage.removeItem('userDOB');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[1040px] p-5 sm:p-6 md:p-12">
      <div className="flex items-center gap-4 md:gap-5">
        <div className="w-16 h-16 md:w-16 md:h-16 rounded-full bg-orange-400 text-white flex items-center justify-center font-poppins text-base font-semibold">
          {initials || 'IM'}
        </div>
        <div className="flex flex-col">
          <span className="text-[#2B3133] font-poppins text-base md:text-base font-medium">
            {fullName || storedName}
          </span>
          <span className="text-[#889696] font-poppins text-sm md:text-sm">
            {email || storedEmail}
          </span>
        </div>
      </div>

      <form
        onSubmit={handleSave}
        className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
      >
        <div className="col-span-1 order-1 md:order-1">
          <label className="text-[#889696] font-poppins text-sm font-medium">
            Full Name
          </label>
          <input
            type="text"
            ref={fullNameRef}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-2 w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins text-sm md:text-base font-normal placeholder:text-sm md:placeholder:text-base placeholder:font-normal"
            disabled={loading}
          />
        </div>

        <div className="col-span-1 order-2 md:order-3">
          <label className="text-[#889696] font-poppins text-sm font-medium">
            Gender
          </label>
          <div className="relative mt-2">
            <select
              ref={genderRef}
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 pr-12 outline-none font-poppins text-[#2B3133] text-sm md:text-base font-normal cursor-pointer appearance-none"
              disabled={loading}
            >
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
            </select>
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2D4343]">
              <svg
                width="14"
                height="8"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                  fill="#2D4343"
                  stroke="#2D4343"
                  strokeWidth="0.5"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="col-span-1 order-3 md:order-2">
          <label className="text-[#889696] font-poppins text-sm font-medium">
            Date of birthday
          </label>
          <div className="mt-2 grid grid-cols-[2fr_1fr_1.5fr] md:grid-cols-[1.4fr_1fr_1fr] gap-2 md:gap-3">
            <div className="relative">
              <select
                ref={monthRef}
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-3 pr-8 md:px-4 md:pr-12 outline-none font-poppins text-[#2B3133] text-sm md:text-base font-normal cursor-pointer appearance-none"
                disabled={loading}
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2D4343] flex flex-col gap-1.5">
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.18411 1.73387L1.2502 5.57318C1.09057 5.7299 1.00098 5.9419 1.00098 6.16288C1.00098 6.38386 1.09057 6.59586 1.2502 6.75258C1.32988 6.83098 1.42467 6.89321 1.52911 6.93567C1.63355 6.97814 1.74557 7 1.85872 7C1.97186 7 2.08388 6.97814 2.18832 6.93567C2.29276 6.89321 2.38755 6.83098 2.46723 6.75258L6.39256 2.9049C6.47224 2.8265 6.56703 2.76428 6.67147 2.72181C6.77591 2.67934 6.88793 2.65748 7.00107 2.65748C7.11422 2.65748 7.22624 2.67934 7.33068 2.72181C7.43512 2.76428 7.52991 2.8265 7.60959 2.9049L11.5349 6.75258C11.6952 6.91009 11.913 6.99901 12.1404 6.9998C12.3678 7.00058 12.5863 6.91316 12.7477 6.75676C12.909 6.60036 13.0002 6.3878 13.001 6.16584C13.0018 5.94387 12.9122 5.73069 12.7519 5.57318L8.81804 1.73387C8.33594 1.26395 7.68244 1 7.00107 1C6.31971 1 5.66621 1.26395 5.18411 1.73387Z"
                    fill="#2D4343"
                    stroke="#2D4343"
                    strokeWidth="0.5"
                  />
                </svg>
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                    fill="#2D4343"
                    stroke="#2D4343"
                    strokeWidth="0.5"
                  />
                </svg>
              </div>
            </div>
            <div className="relative">
              <select
                ref={dayRef}
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-3 pr-8 md:px-4 md:pr-12 outline-none font-poppins text-[#2B3133] text-sm md:text-base font-normal cursor-pointer appearance-none"
                disabled={loading}
              >
                {Array.from({ length: 31 }, (_, i) => `${i + 1}`).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2D4343] flex flex-col gap-1.5">
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.18411 1.73387L1.2502 5.57318C1.09057 5.7299 1.00098 5.9419 1.00098 6.16288C1.00098 6.38386 1.09057 6.59586 1.2502 6.75258C1.32988 6.83098 1.42467 6.89321 1.52911 6.93567C1.63355 6.97814 1.74557 7 1.85872 7C1.97186 7 2.08388 6.97814 2.18832 6.93567C2.29276 6.89321 2.38755 6.83098 2.46723 6.75258L6.39256 2.9049C6.47224 2.8265 6.56703 2.76428 6.67147 2.72181C6.77591 2.67934 6.88793 2.65748 7.00107 2.65748C7.11422 2.65748 7.22624 2.67934 7.33068 2.72181C7.43512 2.76428 7.52991 2.8265 7.60959 2.9049L11.5349 6.75258C11.6952 6.91009 11.913 6.99901 12.1404 6.9998C12.3678 7.00058 12.5863 6.91316 12.7477 6.75676C12.909 6.60036 13.0002 6.3878 13.001 6.16584C13.0018 5.94387 12.9122 5.73069 12.7519 5.57318L8.81804 1.73387C8.33594 1.26395 7.68244 1 7.00107 1C6.31971 1 5.66621 1.26395 5.18411 1.73387Z"
                    fill="#2D4343"
                    stroke="#2D4343"
                    strokeWidth="0.5"
                  />
                </svg>
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                    fill="#2D4343"
                    stroke="#2D4343"
                    strokeWidth="0.5"
                  />
                </svg>
              </div>
            </div>
            <div className="relative">
              <select
                ref={yearRef}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-3 pr-8 md:px-4 md:pr-12 outline-none font-poppins text-[#2B3133] text-sm md:text-base font-normal cursor-pointer appearance-none"
                disabled={loading}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2D4343] flex flex-col gap-1.5">
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.18411 1.73387L1.2502 5.57318C1.09057 5.7299 1.00098 5.9419 1.00098 6.16288C1.00098 6.38386 1.09057 6.59586 1.2502 6.75258C1.32988 6.83098 1.42467 6.89321 1.52911 6.93567C1.63355 6.97814 1.74557 7 1.85872 7C1.97186 7 2.08388 6.97814 2.18832 6.93567C2.29276 6.89321 2.38755 6.83098 2.46723 6.75258L6.39256 2.9049C6.47224 2.8265 6.56703 2.76428 6.67147 2.72181C6.77591 2.67934 6.88793 2.65748 7.00107 2.65748C7.11422 2.65748 7.22624 2.67934 7.33068 2.72181C7.43512 2.76428 7.52991 2.8265 7.60959 2.9049L11.5349 6.75258C11.6952 6.91009 11.913 6.99901 12.1404 6.9998C12.3678 7.00058 12.5863 6.91316 12.7477 6.75676C12.909 6.60036 13.0002 6.3878 13.001 6.16584C13.0018 5.94387 12.9122 5.73069 12.7519 5.57318L8.81804 1.73387C8.33594 1.26395 7.68244 1 7.00107 1C6.31971 1 5.66621 1.26395 5.18411 1.73387Z"
                    fill="#2D4343"
                    stroke="#2D4343"
                    strokeWidth="0.5"
                  />
                </svg>
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                    fill="#2D4343"
                    stroke="#2D4343"
                    strokeWidth="0.5"
                  />
                </svg>
              </div>
            </div>
          </div>
          {dobError ? (
            <p className="text-red-500 text-sm font-poppins mt-1.5">
              {dobError}
            </p>
          ) : null}
        </div>

        <div className="col-span-1 order-4 md:order-4">
          <label className="text-[#889696] font-poppins text-sm font-medium">
            E-mail
          </label>
          <input
            type="email"
            ref={emailRef}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins text-sm md:text-base font-normal placeholder:text-sm md:placeholder:text-base placeholder:font-normal"
            disabled={loading || emailLocked}
          />
        </div>

        {/* Personal Information - only for Patient */}
        {isPatient && (
          <>
            <div className="col-span-1 md:col-span-2 order-5 md:order-5 border-t border-[#E6E6E6] pt-6 mt-1">
              <h2 className="text-[#2B3133] font-poppins font-medium text-[22px]">
                Personal Information
              </h2>
            </div>
            <div className="col-span-1 md:col-span-2 order-6 md:order-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[#889696] font-poppins text-sm font-medium">
                  Street Name
                </label>
                <input
                  type="text"
                  value={streetName}
                  onChange={(e) => setStreetName(e.target.value)}
                  placeholder="Street name"
                  className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins text-sm md:text-base font-normal"
                  disabled={loading}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[#889696] font-poppins text-sm font-medium">
                  Street Number
                </label>
                <input
                  type="text"
                  value={streetNumber}
                  onChange={(e) => setStreetNumber(e.target.value)}
                  placeholder="Street number"
                  className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins text-sm md:text-base font-normal"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-span-1 md:col-span-2 order-7 md:order-7 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[#889696] font-poppins text-sm font-medium">
                  Postal Code
                </label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="Postal code"
                  className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins text-sm md:text-base font-normal"
                  disabled={loading}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[#889696] font-poppins text-sm font-medium">
                  City
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins text-sm md:text-base font-normal"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-span-1 order-8 md:order-8 flex flex-col gap-2">
              <label className="text-[#889696] font-poppins text-sm font-medium">
                Telephone number
              </label>
              <input
                type="tel"
                value={telephoneNumber}
                onChange={(e) => setTelephoneNumber(e.target.value)}
                placeholder="e.g. +31636200935"
                className="w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins text-sm md:text-base font-normal"
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* Security */}
        <div
          className={`col-span-1 md:col-span-2 ${isPatient ? 'order-9 md:order-9' : 'order-5 md:order-5'} border-t border-[#E6E6E6] pt-6 mt-1`}
        >
          <div className="mb-4 flex flex-col items-start gap-3">
            <h2 className="text-[#2B3133] font-poppins font-medium text-[22px]">
              Security
            </h2>
            {!showPasswordFields ? (
              <button
                type="button"
                onClick={() => setShowPasswordFields(true)}
                className="w-auto h-auto rounded-[16px] border border-[#B8B8B8] bg-transparent px-6 py-4 text-left text-[#000000] font-poppins text-lg leading-none font-normal hover:bg-[#F7F7F7] transition-colors"
              >
                Change password
              </button>
            ) : null}
          </div>
        </div>
        {showPasswordFields ? (
          <div className={`col-span-1 md:col-span-2 ${isPatient ? 'order-10 md:order-10' : 'order-6 md:order-6'} grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6`}>
            <div className="flex flex-col gap-2">
              <label className="text-[#889696] font-poppins text-sm font-medium">
                Password
              </label>
              <div className="flex items-center w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
                <input
                  type={showPassword ? 'text' : 'password'}
                  ref={passwordRef}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="@#*%"
                  className="flex-1 bg-transparent outline-none px-5 font-poppins text-sm md:text-base"
                  disabled={loading}
                />
                <div className="w-px h-10 bg-[#CFD9E0]" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
                >
                  {showPassword ? (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-6 h-6"
                    >
                      <path
                        d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z"
                        fill="#889696"
                      />
                      <path
                        d="M2 2L22 22"
                        stroke="#889696"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-6 h-6"
                    >
                      <path
                        d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z"
                        fill="#889696"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {password && password.length < 8 && (
                <p className="text-red-500 text-sm font-poppins mt-1.5">
                  Password must be at least 8 characters
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#889696] font-poppins text-sm font-medium">
                Confirm Password
              </label>
              <div className="flex items-center w-full h-[56px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  ref={confirmPasswordRef}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="@#*%"
                  className="flex-1 bg-transparent outline-none px-5 font-poppins text-sm md:text-base"
                  disabled={loading}
                />
                <div className="w-px h-10 bg-[#CFD9E0]" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
                >
                  {showConfirmPassword ? (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-6 h-6"
                    >
                      <path
                        d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z"
                        fill="#889696"
                      />
                      <path
                        d="M2 2L22 22"
                        stroke="#889696"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-6 h-6"
                    >
                      <path
                        d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z"
                        fill="#889696"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-red-500 text-sm font-poppins mt-1.5">
                  Passwords do not match
                </p>
              )}
            </div>
          </div>
        ) : null}

        <div className={`col-span-1 md:col-span-2 ${isPatient ? 'order-11 md:order-11' : 'order-7 md:order-7'} flex justify-end mt-4 md:mt-2`}>
          <button
            type="submit"
            className="px-8 h-[56px] md:h-[60px] rounded-full bg-[#119975] text-white font-poppins text-base md:text-lg font-medium w-full md:w-auto disabled:opacity-60"
            disabled={loading || saving || isPasswordInvalid}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
